import { describeActor } from './actor';
import { entityTypeText } from './filterChipLabels';
import { usableAuditFilter } from './filterRules';
import { describeAuditTarget, scopeFilter, scopeIdText, scopeStateLabel } from './target';
import { type AuditMessage, type AuditText, auditMessage } from './text';
import type { AuditLogEntry, AuditLogFilters, AuditScopeKind, AuditScopeRef } from './types';
import { AUDIT_ACTOR_TYPES, AUDIT_METHODS } from './types';

/** One line of the details dialog: a label, the full value and a button that filters by it. */
export interface AuditDetailRow {
    /** Stable across entries. */
    id: string;
    label: AuditMessage;
    /** Null when the entry has nothing here; the dialog shows a dash. */
    value: AuditText | null;
    /** A muted aside after the value, such as a scope's id. */
    note: AuditText | null;
    /** What the row's button filters by, or null for no button. */
    filter: AuditLogFilters | null;
    /** The chip label for `filter` when it is not `label`, so the button names what it adds. */
    filterLabel: AuditMessage | null;
}

export interface AuditDetailSections {
    who: AuditDetailRow[];
    target: AuditDetailRow[];
    request: AuditDetailRow[];
}

// Worded like the chip the row's button adds, so the dialog and the chip row agree.
function chipLabel(key: string): AuditMessage {
    return auditMessage(`auditLog.chips.label.${key}`);
}

function idNote(id: number): AuditMessage {
    return auditMessage('auditLog.details.idNote', { id: String(id) });
}

function row(
    id: string,
    label: AuditMessage,
    value: AuditText | null,
    filter: AuditLogFilters | null,
    note: AuditText | null = null
): AuditDetailRow {
    return {
        id,
        label,
        value,
        note,
        filter: value === null || filter === null ? null : usableAuditFilter(filter),
        filterLabel: null,
    };
}

function actorRow(entry: AuditLogEntry): AuditDetailRow {
    const actorType = AUDIT_ACTOR_TYPES.find((known) => known === entry.actorType);
    const type = auditMessage(`auditLog.actorType.${entry.actorType}`, undefined, entry.actorType);
    const id = entry.actorUserId;
    const value =
        id === null ? type : auditMessage('auditLog.chips.value.typedId', { type, id: String(id) });
    // Users and API users share ids, so the id always travels with its type.
    let filter: AuditLogFilters | null = null;
    if (actorType !== undefined) {
        filter =
            id === null ? { actorType: [actorType] } : { actorType: [actorType], actorUserId: id };
    }
    return row('actor', chipLabel('actor'), value, filter);
}

function impersonatorRow(entry: AuditLogEntry): AuditDetailRow {
    const { impersonatorEmail: email, impersonatorUserId: id } = entry;
    const idText =
        id === null ? null : auditMessage('auditLog.actor.impersonatorId', { id: String(id) });
    return row(
        'impersonator',
        chipLabel('impersonator'),
        email ?? idText,
        describeActor(entry).impersonator?.filter ?? null,
        email !== null && id !== null ? idNote(id) : null
    );
}

// The Person filter matches part of either email, so it would also find other people and
// the entries this one impersonated in. When the actor has an id, the Actor row below
// already filters by exactly this person, so this row offers nothing more. An attempted
// sign-in has only the email.
function actedAsRow(entry: AuditLogEntry, actor: AuditDetailRow): AuditDetailRow {
    const email = entry.actorEmail;
    const byId = entry.actorUserId !== null && actor.filter !== null;
    const actedAs = row(
        'actedAs',
        auditMessage('auditLog.details.actedAs'),
        email,
        byId || email === null ? null : { actorEmail: email }
    );
    return actedAs.filter === null ? actedAs : { ...actedAs, filterLabel: chipLabel('person') };
}

function whoRows(entry: AuditLogEntry): AuditDetailRow[] {
    const actor = actorRow(entry);
    return [actedAsRow(entry, actor), actor, impersonatorRow(entry)];
}

function scopeRow(kind: AuditScopeKind, ref: AuditScopeRef): AuditDetailRow {
    const name = ref.name ?? scopeIdText(kind, ref.id);
    const state = scopeStateLabel(ref.state);
    const value =
        state === null ? name : auditMessage('auditLog.chips.value.withState', { name, state });
    let note: AuditMessage | null = null;
    if (ref.state === 'missing') note = auditMessage('auditLog.target.notFound');
    // A row without a name already reads "Client #12".
    // The lookup is made when the log is read, so a renamed scope shows its new name.
    else if (ref.name !== null)
        note = auditMessage('auditLog.details.currentNameNote', { id: String(ref.id) });
    return row(kind, chipLabel(kind), value, scopeFilter(kind, ref.id), note);
}

function targetRows(entry: AuditLogEntry): AuditDetailRow[] {
    const type = entityTypeText(entry.entityType);
    const { entityId, entityLabel } = entry;
    const scopes: Array<[AuditScopeKind, AuditScopeRef | null]> = [
        ['client', entry.client],
        ['location', entry.location],
        ['cage', entry.cage],
    ];
    return [
        row(
            'target',
            chipLabel('target'),
            entityId === null
                ? type
                : auditMessage('auditLog.chips.value.typedId', { type, id: entityId }),
            describeAuditTarget(entry).filter
        ),
        row(
            'entityLabel',
            chipLabel('entityLabel'),
            entityLabel,
            entityLabel === null ? null : { entityLabel }
        ),
        ...scopes.flatMap(([kind, ref]) => (ref === null ? [] : [scopeRow(kind, ref)])),
    ];
}

function requestRow(entry: AuditLogEntry): AuditDetailRow {
    const { method, route } = entry;
    const value =
        method !== null && route !== null
            ? auditMessage('auditLog.summary.request', { method, route })
            : (method ?? route);
    const knownMethod = AUDIT_METHODS.find((known) => known === method);
    // A method the filters cannot name would leave the button matching more than it says.
    const filterable = method === null || knownMethod !== undefined;
    const filter: AuditLogFilters = {};
    if (knownMethod !== undefined) filter.method = [knownMethod];
    if (route !== null) filter.route = route;
    return row(
        'request',
        auditMessage('auditLog.details.request'),
        value,
        filterable ? filter : null
    );
}

function requestRows(entry: AuditLogEntry): AuditDetailRow[] {
    const { requestId, ip, userAgent, statusCode, id } = entry;
    return [
        requestRow(entry),
        row(
            'requestId',
            chipLabel('requestId'),
            requestId,
            requestId === null ? null : { requestId }
        ),
        row('ip', chipLabel('ip'), ip, ip === null ? null : { ip }),
        row(
            'userAgent',
            chipLabel('userAgent'),
            userAgent,
            userAgent === null ? null : { userAgent }
        ),
        row(
            'statusCode',
            chipLabel('statusCode'),
            statusCode === null ? null : String(statusCode),
            statusCode === null ? null : { statusCode }
        ),
        row('entryId', chipLabel('entryId'), id, { entryId: id }),
    ];
}

/**
 * Everything the table leaves out or cuts short, each value in full with the filter it
 * sets: the dialog is where a keyboard user filters by a value.
 */
export function describeAuditDetails(entry: AuditLogEntry): AuditDetailSections {
    return { who: whoRows(entry), target: targetRows(entry), request: requestRows(entry) };
}
